import { type FC, useMemo } from 'react'
import type { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from 'next'
import { type DehydratedState, dehydrate, QueryClient } from '@tanstack/react-query'

import { type GoodDataBetterMarketingPodcastProps, GoodDataBetterMarketingPodcast } from 'views/GoodDataBetterMarketing'

import {
  createEpisodeQuery,
  createRecentEpisodesQuery,
  fetchAllEpisodesSlugs,
} from '@segment/api/routes/goodDataBetterMarketing/queries'

import { createStaticSiteBackend, StaticSiteBackendProvider } from '@segment/contexts/staticSiteBackend'

import type { AppExternalPageProps } from '@segment/next-shared/Providers'

const GoodDataBetterMarketingPage: FC<InferGetStaticPropsType<typeof getStaticProps>> = (props) => {
  const staticSiteBackend = useMemo(() => createStaticSiteBackend(), [])
  return (
    <StaticSiteBackendProvider value={staticSiteBackend}>
      <GoodDataBetterMarketingPodcast {...props} />
    </StaticSiteBackendProvider>
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  const staticSiteBackend = createStaticSiteBackend()
  const slugs = await fetchAllEpisodesSlugs(staticSiteBackend)

  const nonEmptySlugs = slugs.filter((s) => s && s.length > 0)

  return {
    paths: nonEmptySlugs.map((slug) => ({ params: { slug } })),
    fallback: false,
  }
}

export const getStaticProps: GetStaticProps<AppExternalPageProps & GoodDataBetterMarketingPodcastProps> = async (
  ctx
) => {
  const queryClient = new QueryClient()
  const staticSiteBackend = createStaticSiteBackend()

  let episodeSlug: string | null = null

  if (ctx?.params?.slug && !Array.isArray(ctx.params.slug)) {
    episodeSlug = ctx.params.slug
  }

  if (!episodeSlug)
    throw new Error(
      `Missing slug route parameter. Make sure that all slug values returned from getStaticPaths are truthy.`
    )

  await queryClient.prefetchQuery(...createEpisodeQuery(staticSiteBackend, episodeSlug), {
    staleTime: Infinity,
  })

  await queryClient.prefetchQuery(...createRecentEpisodesQuery(staticSiteBackend, episodeSlug), {
    staleTime: Infinity,
  })

  const dangerousDehydratedState = dehydrate(queryClient)

  const safeDehydratedState: DehydratedState = JSON.parse(JSON.stringify(dangerousDehydratedState))

  const props: AppExternalPageProps & GoodDataBetterMarketingPodcastProps = {
    dehydratedState: safeDehydratedState,
    episodeSlug,
  }

  return {
    props,
  }
}

export default GoodDataBetterMarketingPage
